import React, { useState, useEffect, useRef } from "react";
import "./SidebarModal.css";
import { ResizableBox } from "react-resizable";
import * as Icon from "react-feather";
import { Form, Collapse, Accordion } from "react-bootstrap";
import { toast } from "react-toastify";
import LineageVisulaizer from "../../pages/jobs/workflows/lineageVisulaizer";
import { isValidCron } from "cron-validator";
import { createDAGService, fetchWorkflowsService } from "../../services/editor";
import { CircularProgress, Button } from "@mui/material";
import { useAuth } from "../../auth/workosAuth";
import DbtProfileDropdown from "./DbtProfileDropdown";
import { useEditorV2 } from "../../contexts/EditorV2Context";
  

const frequenciesOptions = [
  { value: "Hourly", label: "Hourly" },
  { value: "Daily", label: "Daily" },
  { value: "Custom", label: "Custom Cron" },
  { value: "Relative", label: "After another DAG" }
];

const AlertsOptions = [
  { value: "all", label: "When DAG starts and finishes (Success or Failure)" },
  {
    value: "only_finishes",
    label: "Only when DAG finishes (Success or Failure)",
  },
  { value: "only_failures", label: "Only when DAG fails" },
];

export default function SidebarModal({
  fromV2 = false,
  setIsShown,
  lineageData,
  testModel,
  testData: {
    running_dbt,
    dbt_logs,
    dbt_test_status,
    running_lineage,
    lineage_status,
  },
  skips_,
  dataWarehouses,
  initialComputeWarehouse,
  dbtProfile,
  setDbtProfile,
  dbtProfiles,
  queueOptions
}) {

  const { user } = useAuth(); // Move useAuth hook inside component
  const [userEmail, setUserEmail] = useState(""); // Add state for user email

  // Dark mode detection - only apply when vscode iframe is open
  const { activeVSCode } = useEditorV2();
  const isDarkMode = fromV2 && !!activeVSCode;

    // Add effect to get user email when component mounts
    useEffect(() => {
      if (user && user.email) {
        setUserEmail(user.email);
      }
    }, [user]);

  const [skips, setskips] = useState(skips_);
  useEffect(() => {
    setskips(skips_);
  }, [skips_]);

  const [dag_alias, setdag_alias] = useState(() => {
    return localStorage.getItem('dag_alias') || "";
  });
  const [schedule_dbt_command, setCommand] = useState(() => {
    return localStorage.getItem('schedule_dbt_command') || "";
  });
  const [frequencies, setFrequencies] = useState(() => {
    return localStorage.getItem('frequencies') || "";
  });
  const [cronExpression, setCronExpression] = useState(() => {
    return localStorage.getItem('cronExpression') || "";
  });
  const [retries, setRetries] = useState(() => {
    return parseInt(localStorage.getItem('retries') || "3", 10);
  });
  const [dagTimeout, setDagTimeout] = useState(() => {
    return parseInt(localStorage.getItem('dagTimeout') || "60", 10);
  });
  const [alerts, setAlerts] = useState(() => {
    return localStorage.getItem('alerts') || "";
  });
  const [showTestData, setShowTestData] = useState(false);
  const [isFrequenciesMenuOpen, setFrequenciesMenuOpen] = useState(false);
  const [isAlertsMenuOpen, setAlertsMenuOpen] = useState(false);
  const [isComputeDWHMenuOpen, setComputeDWHMenuOpen] = useState(false);
  const [creatingDag, setCreatingDag] = useState(false);
  const [isDAGsListOpen, setIsDAGsListOpen] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const alertsButtonRef = useRef(null);
  const alertsMenuRef = useRef(null);
  const commandInputRef = useRef(null);

  // Initialize computeWarehouse using props and localStorage
  const [computeWarehouse, setComputeWarehouse] = useState(() => {
    const initialProp = initialComputeWarehouse;
    const firstWarehouse = dataWarehouses && dataWarehouses.length > 0 ? dataWarehouses[0].name : "";
    return initialProp || firstWarehouse || "";
  });

  // Add state for Queue dropdown
  const [queue, setQueue] = useState(() => {
    return 'Default';
  });
  const [isQueueMenuOpen, setIsQueueMenuOpen] = useState(false);

  const handleFrequenciesToggle = (e) => {
    e.stopPropagation();
    // Close all other dropdowns
    setIsDAGsListOpen(false);
    setAlertsMenuOpen(false);
    setComputeDWHMenuOpen(false);
    setIsQueueMenuOpen(false);
    // Toggle this dropdown
    setFrequenciesMenuOpen(!isFrequenciesMenuOpen);
  };

  const handleAlertsToggle = (e) => {
    e.stopPropagation();
    // Close all other dropdowns
    setFrequenciesMenuOpen(false);
    setIsDAGsListOpen(false);
    setComputeDWHMenuOpen(false);
    setIsQueueMenuOpen(false);
    // Toggle this dropdown
    setAlertsMenuOpen(!isAlertsMenuOpen);
  };

  const selectAlerts = (e, val) => {
    setAlerts(val);
    handleAlertsToggle(e);
  };

  const selectFrequencies = (e, val) => {
    setFrequencies(val);
    handleFrequenciesToggle(e);
    setCronExpression("")
  };

  const renderAlertsOptions = () =>
    AlertsOptions.map((alertsOption) => (
      <li
        key={alertsOption.value}
        onClick={(e) => selectAlerts(e, alertsOption.value)}
        className="schedule-menu-item"
      >
        {alertsOption.label}
      </li>
    ));

  const renderFrequenciesOptions = () =>
    frequenciesOptions.map((frequenciesOption) => (
      <li
        key={frequenciesOption.value}
        onClick={(e) => selectFrequencies(e, frequenciesOption.value)}
        className="schedule-menu-item"
      >
        {frequenciesOption.label}
      </li>
    ));

  // Update handler for Compute DWH dropdown toggle
  const handleComputeDWHMenuToggle = (e) => {
    e.stopPropagation();
    // Close all other dropdowns
    setFrequenciesMenuOpen(false);
    setAlertsMenuOpen(false);
    setIsDAGsListOpen(false);
    setIsQueueMenuOpen(false);
    // Toggle this dropdown
    setComputeDWHMenuOpen(!isComputeDWHMenuOpen);
  };

  // Update handler for Queue dropdown toggle
  const handleQueueMenuToggle = (e) => {
    e.stopPropagation();
    // Close all other dropdowns
    setFrequenciesMenuOpen(false);
    setAlertsMenuOpen(false);
    setComputeDWHMenuOpen(false);
    setIsDAGsListOpen(false);
    // Toggle this dropdown
    setIsQueueMenuOpen(!isQueueMenuOpen);
  };

  // Add a handler for DAGs list as well
  const handleDAGsListToggle = (e) => {
    if (e) e.stopPropagation();
    // Close all other dropdowns
    setFrequenciesMenuOpen(false);
    setAlertsMenuOpen(false);
    setComputeDWHMenuOpen(false);
    setIsQueueMenuOpen(false);
    // Toggle this dropdown
    setIsDAGsListOpen(!isDAGsListOpen);
  };

  // Add handler for selecting a Compute DWH
  const selectComputeDWH = (e, wh) => {
    setComputeWarehouse(wh);
    handleComputeDWHMenuToggle(e);
  };

  // Add handler for selecting a Queue
  const selectQueue = (e, q) => {
    setQueue(q);
    handleQueueMenuToggle(e);
  };

  // Save to localStorage when values change
  useEffect(() => {
    localStorage.setItem('dag_alias', dag_alias);
  }, [dag_alias]);

  useEffect(() => {
    localStorage.setItem('schedule_dbt_command', schedule_dbt_command);
  }, [schedule_dbt_command]);

  useEffect(() => {
    localStorage.setItem('frequencies', frequencies);
  }, [frequencies]);

  useEffect(() => {
    localStorage.setItem('cronExpression', cronExpression);
  }, [cronExpression]);

  useEffect(() => {
    localStorage.setItem('retries', retries);
  }, [retries]);

  useEffect(() => {
    localStorage.setItem('dagTimeout', dagTimeout);
  }, [dagTimeout]);

  useEffect(() => {
    localStorage.setItem('alerts', alerts);
  }, [alerts]);

  useEffect(() => {
    localStorage.setItem('skips', JSON.stringify(skips));
  }, [skips]);

  // Save queue to localStorage
  useEffect(() => {
    localStorage.setItem('queue', queue);
  }, [queue]);

  const createDAG = async () => {
    if (!dag_alias || dag_alias === "") {
      toast.info("Please provide a DAG alias");
      return;
    }
    if (!frequencies) {
      toast.info("Please provide the schedule frequency");
      return;
    } else if (
      frequencies === "Custom" &&
      !isValidCron(cronExpression, { alias: true, seconds: true })
    ) {
      toast.info("Invalid cron expression");
      return;
    } else if (frequencies === "Hourly" && !cronExpression) {
      toast.info(
        "Please input the minute you want your DAG to run at every hour"
      );
      return;
    } else if (frequencies === "Hourly" && cronExpression) {
      const minute = parseInt(cronExpression, 10);
      if (isNaN(minute) || minute < 0 || minute > 59) {
        toast.error("Please input a valid minute (0-59) for hourly DAG run");
        return;
      }
    } else if (frequencies === "Daily" && !cronExpression) {
      toast.info("Please input the hour you want your DAG to run at every day");
      return;
    } else if (frequencies === "Daily" && cronExpression) {
      const hour = parseInt(cronExpression, 10);
      if (isNaN(hour) || hour < 0 || hour > 23) {
        toast.error("Please input a valid hour (0-23) for daily DAG run");
        return;
      }
    }
    else if (frequencies === "Relative" && !cronExpression) {
      toast.info("Please select a DAG in the dropdown");
      return;
    }
    if (!/--select|--s/.test(schedule_dbt_command)) {
      toast.info(
        "Your command doesn't seem to be selecting any node. Make sure you have a --select or --s to select relevant nodes."
      );
      return;
    }
    if (/\.sql/.test(schedule_dbt_command)) {
      toast.info(
        "Your command should not contain .sql file extensions. Use model names instead of file names."
      );
      return;
    }
      
    setCreatingDag(true);
    const isSuccess = await createDAGService({
      dag_alias,
        lineage: lineageData,
        schedule_frequency: frequencies,
        cron_expression: cronExpression,
        dag_timeout: dagTimeout,
        retries,
        dbt_command: schedule_dbt_command,
        alerts,
        skips,
        compute_wh: computeWarehouse,
        queue: queue
    })

    if(isSuccess) {
      // Clear localStorage after successful DAG creation
      localStorage.removeItem('dag_alias');
      localStorage.removeItem('schedule_dbt_command');
      localStorage.removeItem('frequencies');
      localStorage.removeItem('cronExpression');
      localStorage.removeItem('retries');
      localStorage.removeItem('dagTimeout');
      localStorage.removeItem('alerts');
      localStorage.removeItem('skips');
      localStorage.removeItem('queue'); // Clear queue from localStorage
      
      setIsShown(false);
    }

    setCreatingDag(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (
        isAlertsMenuOpen &&
        alertsButtonRef.current &&
        alertsMenuRef.current
      ) {
        const buttonRect = alertsButtonRef.current.getBoundingClientRect();
        alertsMenuRef.current.style.top = `${
          buttonRect.bottom + window.scrollY
        }px`;
        alertsMenuRef.current.style.left = `${
          buttonRect.left + window.scrollX
        }px`;
      }
    };

    window.addEventListener("click", () => {
      setFrequenciesMenuOpen(false);
      setAlertsMenuOpen(false);
      setIsQueueMenuOpen(false); // Close queue dropdown on global click
    });
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isAlertsMenuOpen]);

  const fetchWorkflows = async () => {
    const response = await fetchWorkflowsService();

    if(response) {
      setWorkflows(response.data.workflows);
    }
  };

  const selectWorkflow = (DAG) => {
    setCronExpression(DAG);
    setIsDAGsListOpen(false);
  };

  // Add key press handler for Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !running_dbt && schedule_dbt_command) {
      e.preventDefault();
      setShowTestData(true);
      testModel(schedule_dbt_command);
    }
  };

  // Add effect to handle keyboard events
  useEffect(() => {
    const handleGlobalKeyPress = (e) => {
      if (e.key === 'Enter' && !running_dbt && document.activeElement === commandInputRef.current && schedule_dbt_command) {
        e.preventDefault();
        setShowTestData(true);
        testModel(schedule_dbt_command);
      }
    };

    window.addEventListener('keydown', handleGlobalKeyPress);
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyPress);
    };
  }, [running_dbt, schedule_dbt_command, testModel]);

  // Add effect to ensure showTestData is true when tests are running
  useEffect(() => {
    if (running_dbt || running_lineage) {
      setShowTestData(true);
    }
  }, [running_dbt, running_lineage]);

  // Add function to render Compute DWH options
  const renderComputeDWHOptions = () => {
    if (!dataWarehouses || dataWarehouses.length === 0) {
      return <li className="schedule-menu-item disabled">Loading warehouses...</li>;
    }
    return dataWarehouses.map((wh) => (
      <li
        key={wh.name}
        onClick={(e) => selectComputeDWH(e, wh.name)}
        className="schedule-menu-item"
      >
        {wh.name}
      </li>
    ));
  };

  // Add function to render Queue options
  const renderQueueOptions = () => {
    return queueOptions.map((q) => (
      <li
        key={q.name}
        onClick={(e) => selectQueue(e, q.name)}
        className={`schedule-menu-item ${queue === q.name ? 'selected' : ''}`} // Optional: Add selected class if queue is selected
      >
        {q.name}
      </li>
    ));
  };

  return (
    <ResizableBox
      className={`dragged-box react-resizable sidebar-modal-wrapper ${fromV2 ? 'from-v2' : ''} ${isDarkMode ? 'dark-theme' : ''}`}
      height={fromV2 ? window.innerHeight : window.innerHeight - 50}
      width={window.innerWidth / 3}
      maxConstraints={[window.innerWidth - 65, fromV2 ? window.innerHeight : window.innerHeight - 50]}
      minConstraints={[400, 400]}
      resizeHandles={["w"]}
      onResizeStart={() => document.body.classList.add('sidebar-resizing')}
      onResizeStop={() => document.body.classList.remove('sidebar-resizing')}
      id="active-modal-sidebar"
      style={{
        zIndex: 1050,
        position: 'relative',
        ...(fromV2 ? {
          top: 0,
          border: isDarkMode ? '1.2px solid #3c3c3c' : '1.2px solid #dfe0eb',
          borderRadius: 0,
          boxShadow: 'none'
        } : {
          boxShadow: '0 0 10px rgba(0, 0, 0, 0.2)'
        })
      }}
    >
      <section className="schedule-sidebar-content">
        {fromV2 ? (
          // Robin-style header for v2 - matches ChatBot.js exactly
          <h2 className="heading-schedule-model m-0">
            <small
              style={{
                fontWeight: 500,
                fontSize: '12px',
                border: 0,
                borderRadius: 0,
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '10px',
                padding: '16px 15px',
                height: 'auto',
                boxShadow: 'none',
                color: isDarkMode ? '#ffffff' : '#448593',
                borderBottom: isDarkMode ? '1.2px solid #3c3c3c' : '1.2px solid #dfe0eb',
                backgroundColor: isDarkMode ? '#1e1e1e' : 'transparent'
              }}
            >
              <Icon.ChevronsRight
                color={isDarkMode ? '#ffffff' : '#438593'}
                role="button"
                onClick={() => setIsShown(false)}
                size={18}
                style={{ cursor: 'pointer' }}
              />
              Create DAG
            </small>
          </h2>
        ) : (
          // Original v1 header
          <div style={{ marginBottom: '12px', position: 'relative' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '10px',
              borderRadius: '10px',
              background: 'rgba(85, 110, 230, 0.08)',
              color: '#556ee6',
              fontWeight: '600',
              fontSize: '13px',
              letterSpacing: '0.2px',
              boxShadow: '0 2px 6px rgba(85, 110, 230, 0.1)',
              border: '2px solid rgba(19, 25, 59, 0.15)',
              position: 'relative',
              paddingRight: '45px',
              width: '100%'
            }}>
              <Icon.ChevronsRight
                color="#556ee6"
                onClick={() => setIsShown(false)}
                role="button"
                style={{ marginRight: '8px' }}
                size={16}
              />
              <span>Create DAG</span>
              <span style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#2eca6a',
                padding: '2px 6px',
                fontSize: '19px',
              }}>
                🚀
              </span>
            </div>
          </div>
        )}
        <div className={fromV2 ? 'v2-content' : ''}>
        <Form>
          <Accordion defaultActiveKey="1" className="pl-0">
            <Accordion.Item
              eventKey="1"
              className="bg-white"
              style={{
                borderRadius: '18px',
                background: 'linear-gradient(to bottom, #fff, #f9fbfe)',
                boxShadow: '0 8px 20px rgba(85, 110, 230, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05)',
                overflow: 'hidden',
                border: 'none',
                transition: 'all 0.05s ease-in-out'
              }}
            >
              <Accordion.Body
                className="animate__animated animate__fadeIn"
                style={{
                  padding: '18px 25px 22px',
                  backgroundColor: 'rgba(85, 110, 230, 0.03)',
                  borderRadius: '18px',
                  transition: 'all 0.3s ease-in-out'
                }}
                disabled={showTestData && running_dbt}
              >
                <Form.Group controlId="modelName" className="mb-3">
                  <Form.Label 
                    style={{ 
                      fontSize: '11px', 
                      fontWeight: '500', 
                      color: '#495057',
                    }}
                  >
                    DAG alias
                  </Form.Label>
                  <Form.Control
                    value={dag_alias}
                    onChange={(e) => setdag_alias(e.target.value)}
                    type="text"
                    placeholder="Enter DAG alias"
                    style={{fontSize: "11px"}}
                  />
                </Form.Group>

                <Form.Group controlId="schedule_freq">
                  <Form.Label 
                    style={{ 
                      fontSize: '11px', 
                      fontWeight: '500', 
                      color: '#495057',
                    }}
                  >
                    Schedule Frequency
                  </Form.Label>
                  <div
                    style={{ position: "relative", display: "flex", gap: "10px" }}
                  >
                    <div style={{ flex: 1, position: "relative" }}>
                      <Button
                        onClick={handleFrequenciesToggle}
                        aria-controls="schedule-collapse-frequencies"
                        aria-expanded={isFrequenciesMenuOpen}
                        sx={{ 
                          width: "100%",
                          flex: 1,
                          justifyContent: 'space-between',
                          fontSize: "11px",
                          bgcolor: "#fff",
                          color: frequencies ? "#495057" : "grey",
                          border: "1px solid #ebebeb",
                          marginBottom: "15px",
                          height: "36px",

                          "&:hover": {
                            bgcolor: "#ebebeb",
                            color: "#000",
                            borderColor: "#3266bb"
                          }
                        }}
                      >
                        {frequencies
                          ? frequenciesOptions.find(
                              (opt) => opt.value === frequencies
                            ).label
                          : "Select schedule"}
                      </Button>
                      <Collapse in={isFrequenciesMenuOpen}>
                        <ul
                          id="schedule-collapse-frequencies"
                          className="schedule-menu"
                        >
                          {renderFrequenciesOptions()}
                        </ul>
                      </Collapse>
                    </div>
                    {frequencies === "Relative" ? (
                      <div style={{ flex: 1, position: "relative", maxWidth: "calc(50% - 5px)" }}>
                        <Button
                          onClick={() => {
                            if (!workflows.length) {
                              fetchWorkflows();
                            }
                            setFrequenciesMenuOpen(false);
                            setIsDAGsListOpen((value) => !value);
                          }}
                          aria-controls="schedule-collapse-dags"
                          aria-expanded={isDAGsListOpen}
                          sx={{ 
                            width: "100%",
                            flex: 1,
                            justifyContent: 'space-between',
                            fontSize: "11px",
                            bgcolor: "#fff",
                            color: cronExpression ? "#495057" : "grey",
                            border: "1px solid #ebebeb",
                            marginBottom: "15px",
                            height: "36px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
  
                            "&:hover": {
                              bgcolor: "#ebebeb",
                              color: "#000",
                              borderColor: "#3266bb"
                            }
                          }}
                        >
                          {cronExpression && workflows.length
                            ? (workflows.find(workflow => workflow.dag_id === cronExpression)?.workflowname || "Select DAG")
                            : "Select DAG"}
                        </Button>
                        <Collapse in={isDAGsListOpen}>
                          <ul id="schedule-collapse-dags" className="schedule-menu">
                            {workflows.length ? (
                              workflows.map((workflow) => (
                                <li
                                  key={workflow.dag_id}
                                  onClick={() => selectWorkflow(workflow.dag_id)}
                                  className="schedule-menu-item"
                                >
                                  {workflow.workflowname}
                                </li>
                              ))
                            ) : (
                              <CircularProgress size={16} color="#fff" />
                            )}
                          </ul>
                        </Collapse>
                      </div>
                    ) : (
                      frequencies === "Custom" ? (
                        <Form.Control
                          type="text"
                          value={cronExpression}
                          onChange={(e) => setCronExpression(e.target.value)}
                          placeholder="Enter cron expression"
                          className={`collapse-schedule ${frequencies && "collapse-schedule-chosen"}`}
                          style={{ flex: 1, fontSize: "11px" }}
                        />
                      ) : (
                        <Form.Control
                          type="number"
                          value={cronExpression}
                          onChange={(e) => setCronExpression(e.target.value)}
                          min={0}
                          disabled={!frequencies}
                          max={frequencies === "Hourly" ? 59 : 23}
                          placeholder={
                            frequencies === "Hourly"
                              ? "Minute to run at (0-59)"
                              : frequencies === "Daily" ? "Hour to run at (0-23)" : "<- Select schedule first"
                          }
                          className={`collapse-schedule ${frequencies && "collapse-schedule-chosen"}`}
                          style={{ flex: 1, fontSize: "11px" }}
                        />
                      )
                    )}
                  </div>
                </Form.Group>

                <Form.Group controlId="alerts">
                  <Form.Label 
                    style={{ 
                      fontSize: '11px', 
                      fontWeight: '500', 
                      color: '#495057',
                    }}
                  >
                    Slack Alert
                  </Form.Label>
                  <div style={{ position: "relative" }}>
                    <Button
                      ref={alertsButtonRef}
                      onClick={handleAlertsToggle}
                      aria-controls="schedule-collapse-alerts"
                      aria-expanded={isAlertsMenuOpen}
                      sx={{ 
                        width: "100%",
                        flex: 1,
                        justifyContent: 'space-between',
                        fontSize: "11px",
                        bgcolor: "#fff",
                        color: alerts ? "#495057" : "grey",
                        border: "1px solid #ebebeb",
                        marginBottom: "15px",
                        height: "36px",
  
                        "&:hover": {
                          bgcolor: "#ebebeb",
                          color: "#000",
                          borderColor: "#3266bb"
                        }
                      }}
                    >
                      {alerts
                        ? AlertsOptions.find((opt) => opt.value === alerts).label
                        : ""}
                    </Button>
                    <Collapse in={isAlertsMenuOpen}>
                      <ul
                        id="schedule-collapse-alerts"
                        className="schedule-menu"
                        ref={alertsMenuRef}
                      >
                        {renderAlertsOptions()}
                      </ul>
                    </Collapse>
                  </div>
                </Form.Group>

                <Form.Group controlId="dependencies">
                  <Form.Label 
                    style={{ 
                      fontSize: '11px', 
                      fontWeight: '500', 
                      color: '#495057',
                    }}
                  >
                    DBT Command to build DAG from
                  </Form.Label>
                  <div className="d-flex align-items-center">
                    <Form.Control
                      ref={commandInputRef}
                      value={schedule_dbt_command}
                      onChange={(e) => setCommand(e.target.value)}
                      onKeyDown={handleKeyPress}
                      type="text"
                      placeholder="Enter DBT Command"
                      style={{fontSize: "11px"}}
                    />
                    <DbtProfileDropdown
                      dbtProfile={dbtProfile}
                      dbtProfiles={dbtProfiles}
                      setDbtProfile={setDbtProfile}
                      className="test-button p-0 border border-white"
                      sx={{
                        height: "34px"
                      }}
                      darkMode={isDarkMode}
                    />
                    <Button
                      onClick={() => {
                        setShowTestData(true);
                        testModel(schedule_dbt_command);
                      }}
                      variant="contained"
                      disabled={showTestData && running_dbt}
                      sx={{
                        fontSize: "11px",
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        borderTopRightRadius: '8px',
                        borderBottomRightRadius: '8px',
                        height: '34px',
                        padding: '0 12px',
                        bgcolor: "#3F80EA",
                        border: "1px solid #fff",
                        
                        "&:hover": {
                          bgcolor: "#366dc7",
                        },
                        "&:disabled": {
                          bgcolor: "#3F80EA",
                          color: "#fff"
                        }
                      }}
                    >
                      <Icon.Code width={15} className="button-icon" />
                      {running_dbt ? "Testing..." : "Test"}
                    </Button>
                  </div>
                </Form.Group>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
          
          <br />
          <Accordion defaultActiveKey="0" className="pl-0 mb-3">
            <Accordion.Item
              eventKey="1"
              className="bg-white"
              style={{
                borderRadius: '18px',
                background: 'linear-gradient(to bottom, #fff, #f9fbfe)',
                boxShadow: '0 8px 20px rgba(85, 110, 230, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05)',
                overflow: 'hidden',
                border: 'none',
                transition: 'all 0.05s ease-in-out'
              }}
            >
              <Accordion.Header
                style={{
                  background: 'transparent',
                  border: 'none',
                  width: '100%',
                  borderRadius: '18px 18px 0 0',
                  transition: 'all 0.3s ease'
                }}
              >
                <div 
                  className="d-flex align-items-center w-100" 
                  style={{ 
                    fontWeight: '600', 
                    fontSize: '12px', 
                    color: '#556ee6',
                    letterSpacing: '0.2px'
                  }}
                >
                  {/* Conditional icon container based on status */}
                  {dbt_test_status === "success" ? (
                    <div style={{
                      backgroundColor: 'rgba(46, 202, 106, 0.15)',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '12px',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 5px rgba(46, 202, 106, 0.2)'
                    }}>
                      <Icon.Check color="#2eca6a" size={16} />
                    </div>
                  ) : dbt_test_status === "failed" ? (
                    <div style={{
                      backgroundColor: 'rgba(241, 85, 85, 0.15)',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '12px',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 5px rgba(241, 85, 85, 0.2)'
                    }}>
                      <Icon.X color="#f15555" size={16} />
                    </div>
                  ) : running_dbt ? (
                    <div style={{
                      backgroundColor: 'rgba(85, 110, 230, 0.1)',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '12px',
                      transition: 'all 0.2s ease'
                    }}>
                      <CircularProgress 
                        size={18} 
                        style={{ 
                          color: '#556ee6',
                          animation: 'rotate 1.5s linear infinite'
                        }} 
                      />
                    </div>
                  ) : (
                    <div 
                      style={{
                        backgroundColor: 'rgba(85, 110, 230, 0.1)',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '12px'
                      }}
                    >
                      <Icon.Code size={16} color="#556ee6" />
                    </div>
                  )}
                  
                  {running_dbt ? (
                    <span 
                      style={{
                        animation: 'pulseText 1.5s infinite ease-in-out',
                        display: 'inline-flex',
                        alignItems: 'center',
                        minWidth: '180px' // Prevent layout shifts
                      }}
                    >
                      Testing your DBT command
                      <span 
                        style={{
                          width: '18px',
                          textAlign: 'left',
                          display: 'inline-block',
                          animation: 'ellipsis 1.5s infinite steps(4, end)',
                          overflow: 'hidden'
                        }}
                      >
                        ...
                      </span>
                    </span>
                  ) : (
                    "DBT Test Logs"
                  )}
                </div>
              </Accordion.Header>
              {dbt_logs && (
              <Accordion.Body 
              className="animate__animated animate__fadeIn" 
              style={{ 
                backgroundColor: 'rgba(85, 110, 230, 0.03)',
                borderTop: '1px solid rgba(85, 110, 230, 0.05)',
                borderRadius: '0 0 18px 18px',
                transition: 'all 0.3s ease-in-out',
              }}
            >
                <div
                    className="log-display"
                    dangerouslySetInnerHTML={{
                      __html: dbt_logs,
                    }}
                  />
                </Accordion.Body>
              )}
            </Accordion.Item>
          </Accordion>
          <Accordion defaultActiveKey="0" className="pl-0 mb-3">
          <Accordion.Item
              eventKey="1"
              className="bg-white"
              style={{
                borderRadius: '18px',
                background: 'linear-gradient(to bottom, #fff, #f9fbfe)',
                boxShadow: '0 8px 20px rgba(85, 110, 230, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05)',
                overflow: 'hidden',
                border: 'none',
                transition: 'all 0.05s ease-in-out'
              }}
            >
              <Accordion.Header
                style={{
                  background: 'transparent',
                  border: 'none',
                  width: '100%',
                  borderRadius: '18px 18px 0 0',
                  transition: 'all 0.3s ease'
                }}
              >
                <div 
                  className="d-flex align-items-center w-100" 
                  style={{ 
                    fontWeight: '600', 
                    fontSize: '12px', 
                    color: '#556ee6',
                    letterSpacing: '0.2px'
                  }}
                >
                  {/* Conditional icon container based on status */}
                  {lineage_status === "success" ? (
                    <div style={{
                      backgroundColor: 'rgba(46, 202, 106, 0.15)',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '12px',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 5px rgba(46, 202, 106, 0.2)'
                    }}>
                      <Icon.Check color="#2eca6a" size={16} />
                    </div>
                  ) : lineage_status === "failed" ? (
                    <div style={{
                      backgroundColor: 'rgba(241, 85, 85, 0.15)',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '12px',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 5px rgba(241, 85, 85, 0.2)'
                    }}>
                      <Icon.X color="#f15555" size={16} />
                    </div>
                  ) : running_lineage ? (
                    <div style={{
                      backgroundColor: 'rgba(85, 110, 230, 0.1)',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '12px',
                      transition: 'all 0.2s ease'
                    }}>
                      <CircularProgress 
                        size={18} 
                        style={{ 
                          color: '#556ee6',
                          animation: 'rotate 1.5s linear infinite'
                        }} 
                      />
                    </div>
                  ) : (
                    <div 
                      style={{
                        backgroundColor: 'rgba(85, 110, 230, 0.1)',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '12px'
                      }}
                    >
                      <Icon.Layers size={16} color="#556ee6" />
                    </div>
                  )}
                  
                  {running_lineage ? (
                    <span 
                      style={{
                        animation: 'pulseText 1.5s infinite ease-in-out',
                        display: 'inline-flex',
                        alignItems: 'center',
                        minWidth: '180px' // Prevent layout shifts
                      }}
                    >
                      Calculating DAG visualisation
                      <span 
                        style={{
                          width: '18px',
                          textAlign: 'left',
                          display: 'inline-block',
                          animation: 'ellipsis 1.5s infinite steps(4, end)',
                          overflow: 'hidden'
                        }}
                      >
                        ...
                      </span>
                    </span>
                  ) : (
                    "DAG Visualisation"
                  )}
                </div>
              </Accordion.Header>            
              {lineageData.initial_workflow_chain?.length > 0 && (
              <Accordion.Body 
              className="animate__animated animate__fadeIn" 
              style={{ 
                padding: '18px 25px 22px',
                backgroundColor: 'rgba(85, 110, 230, 0.03)',
                borderTop: '1px solid rgba(85, 110, 230, 0.05)',
                borderRadius: '0 0 18px 18px',
                transition: 'all 0.3s ease-in-out'
              }}
            >
              <LineageVisulaizer
                    editWorkflow={lineageData}
                    style={{ height: "50vh" }}
                  />
                </Accordion.Body>
              )}
            </Accordion.Item>
          </Accordion>
        </Form>

        <Accordion defaultActiveKey="1" className="pl-0 mb-3">
        <Accordion.Item
              eventKey="0"
              className="bg-white"
              style={{
                borderRadius: '18px',
                background: 'linear-gradient(to bottom, #fff, #f9fbfe)',
                boxShadow: '0 8px 20px rgba(85, 110, 230, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05)',
                overflow: 'hidden',
                border: 'none',
                transition: 'all 0.05s ease-in-out'
              }}
            >
              <Accordion.Header
                style={{
                  background: 'transparent',
                  border: 'none',
                  width: '100%',
                  borderRadius: '18px 18px 0 0',
                  transition: 'all 0.3s ease'
                }}
              >
                <div 
                  className="d-flex align-items-center w-100" 
                  style={{ 
                    fontWeight: '600', 
                    fontSize: '12px', 
                    color: '#556ee6',
                    letterSpacing: '0.2px'
                  }}
                >
                  <div 
                    style={{
                      backgroundColor: 'rgba(85, 110, 230, 0.1)',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '12px'
                    }}
                  >
                    <Icon.Settings size={16} color="#556ee6" />
                  </div>
                  Advanced Settings
                  <span 
                    className="ms-auto" 
                    style={{
                      transform: 'translateY(-1px)',
                      opacity: 0.7,
                      fontWeight: 'normal',
                      fontSize: '12px'
                    }}
                  >
                  </span>
                </div>
              </Accordion.Header>
              <Accordion.Body 
                className="animate__animated animate__fadeIn" 
                style={{ 
                  padding: '18px 25px 22px',
                  backgroundColor: 'rgba(85, 110, 230, 0.03)',
                  borderTop: '1px solid rgba(85, 110, 230, 0.05)',
                  borderRadius: '0 0 18px 18px',
                  transition: 'all 0.3s ease-in-out'
                }}
              >

              <div className="d-flex gap-2">
                <Form.Group controlId="compute_dwh" style={{ flex: 1 }}>
                  <Form.Label 
              style={{ 
                fontSize: '11px', 
                fontWeight: '500', 
                color: '#495057',
              }}
            >
              Compute DWH</Form.Label>
                  <div style={{ position: "relative" }}>
                    <Button
                      onClick={handleComputeDWHMenuToggle}
                      aria-controls="schedule-collapse-compute-dwh"
                      aria-expanded={isComputeDWHMenuOpen}
                      variant="contained"
                      sx={{
                        padding: "0.25rem 0.7rem",
                        width: "100%",
                        backgroundColor: "white",
                        color: computeWarehouse ? "#495057" : "grey",
                        border: "1px solid #ebebeb",
                        justifyContent: "flex-start",
                        marginBottom: "15px",
                        height: "36px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        fontSize: "11px",

                        "&:hover": {
                          backgroundColor: "#ebebeb",
                          color: "black",
                          borderColor: "#3266bb",
                          boxShadow: "none",
                        },
                        "&:active": {
                          boxShadow: "none"
                        }
                      }}
                    >
                      {computeWarehouse || "Select Compute DWH"}
                    </Button>
                    <Collapse in={isComputeDWHMenuOpen}>
                      <ul
                        id="schedule-collapse-compute-dwh"
                        className="schedule-menu"
                      >
                        {renderComputeDWHOptions()}
                      </ul>
                    </Collapse>
                  </div>
                </Form.Group>
                <Form.Group controlId="queue" style={{ flex: 1 }}>
                  <Form.Label 
                    style={{ 
                      fontSize: '11px', 
                      fontWeight: '500', 
                      color: '#495057',
                    }}
                  >
                    Worker Queue</Form.Label>
                    <div style={{ position: "relative" }}>
                      <Button
                        onClick={handleQueueMenuToggle} // Use queue toggle handler
                        aria-controls="schedule-collapse-queue" // Set aria controls
                        aria-expanded={isQueueMenuOpen} // Set aria expanded
                        sx={{ 
                          width: "100%",
                          flex: 1,
                          justifyContent: 'space-between',
                          fontSize: "11px",
                          bgcolor: "#fff",
                          color: queue ? "#495057" : "grey",
                          border: "1px solid #ebebeb",
                          marginBottom: "15px",
                          height: "36px",

                          "&:hover": {
                            bgcolor: "#ebebeb",
                            color: "#000",
                            borderColor: "#3266bb"
                          }
                        }}
                      >
                        {queue || "Select Queue"} {/* Display selected queue or placeholder */}
                      </Button>
                      {/* Add Collapse component for Queue options */}
                      <Collapse in={isQueueMenuOpen}>
                        <ul
                          id="schedule-collapse-queue"
                          className="schedule-menu"
                        >
                          {renderQueueOptions()} {/* Render queue options */}
                        </ul>
                      </Collapse>
                    </div>
                  </Form.Group>
              </div>
              <div className="d-flex gap-2">
                <Form.Group controlId="dag_timeout" style={{ flex: 1 }}>
                  <Form.Label 
                    style={{ 
                      fontSize: '11px', 
                      fontWeight: '500', 
                      color: '#495057',
                    }}
                  >
                  DAG Timeout (in minutes)</Form.Label>
                  <Form.Control
                    type="number"
                    value={dagTimeout}
                    onChange={(e) => setDagTimeout(e.target.value)}
                    placeholder="Your DAG will automatically fail after this time (default: 60)"
                    style={{fontSize: "11px"}}
                  />
                </Form.Group>
                <Form.Group controlId="retries" style={{ flex: 1 }}>
                  <Form.Label 
                    style={{ 
                      fontSize: '11px', 
                      fontWeight: '500', 
                      color: '#495057',
                    }}
                  >
                    Retries
                  </Form.Label>
                  <Form.Control
                    type="number"
                    value={retries}
                    onChange={(e) => setRetries(e.target.value)}
                    placeholder="Retries before considering the DAG failed (default: 3)"
                    style={{fontSize: "11px"}}
                  />
                </Form.Group>
              </div><br/>
              <Form.Group controlId="sources">
                <Form.Label 
              style={{ 
                fontSize: '11px', 
                fontWeight: '500', 
                color: '#495057',
              }}
            >
              Skip Sources (in minutes)
              <br/>
                      <span 
                        style={{ 
                          fontWeight: '400', 
                          opacity: 0.7, 
                          fontSize: '10px'
                        }}
                      >
                        0 minutes default means never skip
                      </span>
              </Form.Label>
                {Object.keys(skips).length > 0 ? (
                  Object.keys(skips).map((sourceName) => (
                    <div key={sourceName} className="d-flex mb-2">
                      <Form.Control
                        type="text"
                        value={sourceName}
                        disabled
                        className="mr-2"
                        style={{ width: '80%', fontSize: "11px" }}
                      />
                      <Form.Control
                        type="number"
                        value={skips[sourceName]}
                        onChange={(e) => {
                          const updatedSkips = { ...skips };
                          updatedSkips[sourceName] = e.target.value === '' ? '' : parseInt(e.target.value, 10);
                          setskips(updatedSkips);
                        }}
                        style={{ width: '40%', fontSize: "11px" }}
                      />
                    </div>
                  ))
                ) : running_dbt || running_lineage ? (<div 
                  style={{
                    padding: '12px',
                    borderRadius: '10px',
                    backgroundColor: 'rgba(85, 110, 230, 0.05)',
                    textAlign: 'center',
                    fontSize: '13px',
                    color: '#556ee6'
                  }}>Waiting for test and lineage to finish to identify sources...</div>):
                (
                  <div 
                  style={{
                    padding: '12px',
                    borderRadius: '10px',
                    backgroundColor: 'rgba(85, 110, 230, 0.05)',
                    textAlign: 'center',
                    fontSize: '13px',
                    color: '#556ee6'
                  }}>No source identified</div>
                )}
              </Form.Group>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>

        <Button
          disabled={(userEmail !== "rob@datajolt.io" && userEmail !== "max@datajolt.io") ? 
            (dbt_test_status !== "success" || creatingDag) : 
            creatingDag}
          onClick={createDAG}
          variant="contained"
          sx={{
            borderRadius: "5px",
            width: "100%",
            backgroundColor: "#54cb75",
            color: "#fff",
            padding: "12px 16px",

            "&:hover": {
              backgroundColor: "#54cb75",
              opacity: 0.8,
              boxShadow: "none",
            },
            "&:active": {
              boxShadow: "none"
            },
            "&:disabled": {
              backgroundColor: isDarkMode ? "#2d2d2d" : "gray",
              color: isDarkMode ? "#858585" : "white"
            }
          }}
        >
          <div style={{ 
            display: "flex", 
            justifyContent: "center", 
            alignItems: "center", 
            width: "100%",
            position: "relative"
          }}>
            <span style={{ 
              fontWeight: "800", 
              letterSpacing: "0.3px", 
              fontSize: "12px",
              textAlign: "center"
            }}>
              Create my DAG
            </span>
            
            {(userEmail === "rob@datajolt.io" || userEmail === "max@datajolt.io") && (
              <span 
                style={{
                  marginLeft: "8px",
                  background: "#FF5722",
                  color: "white",
                  padding: "2px 6px",
                  borderRadius: "10px",
                  fontSize: "10px",
                  fontWeight: "bold"
                }}
              >
                ADMIN
              </span>
            )}
            
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              borderRadius: "50%",
              padding: "5px",
              height: "24px",
              width: "24px",
              position: "absolute",
              right: "0"
            }}>
              <svg 
                stroke="currentColor" 
                fill="currentColor" 
                strokeWidth="0" 
                viewBox="0 0 512 512" 
                height="0.9em" 
                width="0.9em" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M505.12019,19.09375c-1.18945-5.53125-6.65819-11-12.207-12.1875C460.716,0,435.507,0,410.40747,0,307.17523,0,245.26909,55.20312,199.05238,128H94.83772c-16.34763.01562-35.55658,11.875-42.88664,26.48438L2.51562,253.29688A28.4,28.4,0,0,0,0,264a24.00867,24.00867,0,0,0,24.00582,24H127.81618l-22.47457,22.46875c-11.36521,11.36133-12.99607,32.25781,0,45.25L156.24582,406.625c11.15623,11.1875,32.15619,13.15625,45.27726,0l22.47457-22.46875V488a24.00867,24.00867,0,0,0,24.00581,24,28.55934,28.55934,0,0,0,10.707-2.51562l98.72834-49.39063c14.62888-7.29687,26.50776-26.5,26.50776-42.85937V312.79688c72.59753-46.3125,128.03493-108.40626,128.03493-211.09376C512.07526,76.5,512.07526,51.29688,505.12019,19.09375ZM384.04033,168A40,40,0,1,1,424.05,128,40.02322,40.02322,0,0,1,384.04033,168Z"></path>
              </svg>
            </div>
          </div>
        </Button>
        </div>
      </section>
    </ResizableBox>
  );
}


